import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ChatExportMenu.module.css';
import chatAgentService from '../../services/RealChatAgentService';
import { exportChat } from '../../utils/chatExport/exportChat';

/**
 * TRAC-848: "download your chat" — the customer-facing control.
 *
 * Deliberately NOT wrapped in `shouldShowDevControls`. This ships in every
 * environment to every customer; a control that only we can see would make the
 * whole lane pointless. It lives in EmbedPage's `widgetHeader`, the chat's only
 * header on every surface (TRAC-991 removed ChatInterface's never-rendered copy).
 *
 * Miles, 2026-07-29: PDF only, and the button must confirm before downloading.
 * So this is a confirm step rather than a format picker — the same
 * confirm-tooltip shape as "Clear conversation" in EmbedPage, so the two header
 * controls behave consistently.
 *
 * Source of the conversation: `chatAgentService.getMessagesFromSession()`.
 *
 * NOT `useChatStore` — despite the store exposing a `messages` array, nothing
 * writes conversation messages to it. ChatInterface keeps them in local
 * component state and persists them through the service; the store is only used
 * for experiment assignments. Reading the store here returned an empty array and
 * every export came back "no conversation to download".
 *
 * The service accessor is the right source anyway: it is namespaced per surface,
 * rehydrates `Date` fields, and is exactly what a page reload reads — so what the
 * customer downloads matches what they would see after refreshing.
 *
 * No history endpoint: embed customers are not signed in, so a session-keyed
 * server read would be an IDOR surface.
 */

interface ChatExportMenuProps {
  /** Disabled mid-stream so an export can't capture half of an in-flight answer. */
  disabled?: boolean;
}

const ChatExportMenu: React.FC<ChatExportMenuProps> = ({ disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setStatus(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, close]);

  const handleConfirm = () => {
    const outcome = exportChat(chatAgentService.getMessagesFromSession(), {
      // The service knows when a persist failed (quota, private browsing). Carry
      // that fact into the export so the customer is told their copy may be
      // short rather than handed a confidently-truncated file.
      knownPersistenceGap: chatAgentService.persistenceDegraded
        ? 'Some of this conversation could not be saved in your browser, so part of it may be missing.'
        : undefined,
    });

    if (outcome.ok) {
      close();
      return;
    }

    switch (outcome.reason) {
      case 'empty':
        setStatus('There is no conversation to download yet.');
        break;
      case 'print-unavailable':
        setStatus('We could not open the print view, so we downloaded a web page instead — open it and print from there.');
        break;
      default:
        setStatus('Something went wrong preparing your download. Please try again.');
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={styles.trigger}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        disabled={disabled}
        title="Download this chat"
        aria-label="Download this chat"
        aria-expanded={isOpen}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.confirm}>
          <div className={styles.confirmText}>Download this chat as a PDF?</div>
          {status && <div className={styles.status}>{status}</div>}
          <div className={styles.confirmButtons}>
            <button className={styles.confirmButton} onClick={handleConfirm}>
              Download
            </button>
            <button className={styles.cancelButton} onClick={close}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatExportMenu;
